/*!
  Pioneer WFRIA Section v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Section"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Section") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Section");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["className", "title", "headingLevel", "header", "kind", "expanded", "defaultExpanded", "children", "standalone", "disabled", "highlighted", "highlightedMessage", "aria-labelledby", "onExpandedChange", "onToggleButtonKeyDown", "lazyLoad", "transitionDuration", "iconExpand", "iconCollapse", "customStrings"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DELAY_TIME } from "../common/constants";
import { useControlOrUnControl, useUniqueId } from "../common/hooks";
import { requiredIf, validIcon } from "../common/proptypes";
import ExpandableTransition from "../private/ExpandableTransition/ExpandableTransition";
import LiveRegion from "../private/LiveRegion/LiveRegion";
import Tooltip from "../private/Tooltip/Tooltip";
import { SECTION_HEADER_FOCUS_CLASS } from "../themes/common/classes";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import { Button, IconCollapse, IconError, IconExpand } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const EXPAND_ONLY = "expandOnly";
const POLITE = "polite";
/**
 * Section is a React component composed of two containers - Header & Body.
 * The Header is a controlled/closed container that can contain specific items
 * like Titles, Buttons, Links, Controls and Labels. The Body is an open-ended
 * container that has no limitations to what components can be housed in it (i.e., Table, Form Controls etc.).
 */

function Section(_ref) {
  let {
    className,
    title,
    headingLevel: Heading,
    header,
    kind,
    expanded: propsExpanded,
    defaultExpanded,
    children,
    standalone,
    disabled,
    highlighted,
    highlightedMessage,
    "aria-labelledby": ariaLabelledby,
    onExpandedChange,
    onToggleButtonKeyDown,
    lazyLoad,
    transitionDuration,
    iconExpand,
    iconCollapse,
    customStrings
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssHeader,
    cssHeaderContent,
    cssHeaderContainer,
    cssBody,
    cssBodyContent,
    cssErrorIcon,
    cssToggleButton
  } = extractTheme({
    theme,
    standalone,
    highlighted,
    kind,
    disabled
  });
  const uniqueId = useUniqueId();
  const expandedOnceRef = useRef(propsExpanded || !lazyLoad && typeof lazyLoad === "boolean" || defaultExpanded);
  /**
   * useControlOrUncontrol hook returns the getter and setter
   * methods for updating the prop value.
   */

  const [getExpanded, setExpanded] = useControlOrUnControl({
    unControlledPropValue: defaultExpanded,
    controlledPropValue: propsExpanded,
    changeHandler: onExpandedChange
  });
  const toggleRef = useRef();
  const [liveRegionText, setLiveRegionText] = useState("");
  const componentStrings = useMemo(() => _objectSpread(_objectSpread({}, STRINGS), customStrings), [customStrings]);
  /**
   * With type = disclosureList, toggle button focus sets a focus class
   * on the header.
   */

  const [toggleButtonHasFocus, setToggleButtonHasFocus] = useState(false);
  /**
   * setLiveRegionText will update the live region content on expand/collapse of section.
   */

  const setLiveRegion = useCallback(expanded => {
    const {
      collapsedAnnouncement,
      expandedAnnouncement
    } = componentStrings;
    const liveRegionText = expanded ? expandedAnnouncement(title) : collapsedAnnouncement(title);
    setLiveRegionText(liveRegionText);
  }, [componentStrings, title]);
  /**
   * handleToggleSection will update the state(expand/collapse) of section `onClick` of toggle button.
   * And invokes the callbacks passed through `onExpandedChange` prop.
   */

  const handleToggleSection = () => {
    const newExpanded = !getExpanded();
    setLiveRegion(newExpanded);
    setExpanded(newExpanded);
  };
  /**
   * When the header is clicked with type = disclosureList, focus the toggle
   * button and expand or collapse the Section.
   */


  const handleHeaderClick = () => {
    setToggleButtonHasFocus(true);
    handleToggleSection();
    toggleRef.current.focus();
  };
  /**
   * Set toggle button focus to state in type = disclosureList
   * so that focus class can be added to header.
   */


  const handleToggleFocus = () => {
    setToggleButtonHasFocus(true);
  };
  /**
   * Set toggle button blurred to state with type = disclosureList
   * so that focus class can be removed from header.
   */


  const handleToggleBlur = () => {
    setToggleButtonHasFocus(false);
  };
  /**
   * Set focus and blur event listeners to toggle button with
   * type = disclosureList so that focus state can be used to set
   * focus class on header.
   */


  useEffect(() => {
    const toggle = toggleRef.current;

    if (toggle && kind === "disclosureList") {
      toggle.addEventListener("focus", handleToggleFocus);
      toggle.addEventListener("blur", handleToggleBlur);
    }

    return () => {
      if (toggle) {
        toggle.removeEventListener("focus", handleToggleFocus);
        toggle.removeEventListener("blur", handleToggleBlur);
      }
    };
  }, [toggleRef, kind]);
  /**
   * Handles changes of expanded prop in controlled component.
   */

  useEffect(() => {
    if (propsExpanded !== null) {
      setLiveRegion(propsExpanded);
    }
  }, [propsExpanded, setLiveRegion]);
  /**
   * Clears the live region announcement text.
   */

  const clearAnnouncement = () => {
    setLiveRegionText("");
  };
  /**
   * @description Used to render the Section content based on lazyLoad prop value
   * @param {node} child content of Section component.
   * @param {boolean} expanded holds the expanded state of Section.
   * @param {boolean|string} lazyLoad lazyLoad prop of Section.
   */


  const renderContent = (child, expanded, lazyLoad) => {
    if (lazyLoad && expanded && !expandedOnceRef.current) {
      expandedOnceRef.current = true;
    }

    if (lazyLoad === EXPAND_ONLY) {
      if (expanded) return child;
    } else if (expandedOnceRef.current) {
      return child;
    }
  };

  const toggleIcon = getExpanded() ? iconCollapse : iconExpand;
  const sectionClassNames = clsx("pnr-section", className);
  const headerClassNames = clsx("pnr-section__header", {
    [SECTION_HEADER_FOCUS_CLASS]: toggleButtonHasFocus
  });
  const onlyTitle = !header && title;
  const titleId = onlyTitle ? `pnr-section-header-${uniqueId}` : null;
  const headerId = ariaLabelledby || titleId;
  const sectionAriaLabel = componentStrings.expandButtonLabel(title, getExpanded());
  return _jsxs("section", _objectSpread(_objectSpread({
    className: sectionClassNames,
    "aria-labelledby": headerId,
    css: cssRoot
  }, otherProps), {}, {
    children: [_jsxs("div", {
      className: headerClassNames,
      css: cssHeader,
      onClick: kind === "disclosureList" && !disabled ? handleHeaderClick : null,
      children: [_jsxs("div", {
        css: cssHeaderContainer,
        children: [(header || title) && _jsxs("div", {
          id: onlyTitle ? headerId : null,
          className: "pnr-section__header-content",
          css: cssHeaderContent,
          children: [header, onlyTitle && _jsx(React.Fragment, {
            children: titleId && Heading ? _jsx(Heading, {
              children: title
            }) : _jsx("span", {
              children: title
            })
          })]
        }), !standalone && _jsxs(React.Fragment, {
          children: [_jsx(Tooltip, {
            label: sectionAriaLabel,
            trigger: _jsx(Button, {
              kind: "chromeless",
              "aria-controls": `pnr-section-body-${uniqueId}`,
              className: "pnr-section__toggle",
              centerIcon: toggleIcon,
              onClick: handleToggleSection,
              onKeyDown: onToggleButtonKeyDown,
              disabled: disabled,
              "aria-label": sectionAriaLabel,
              css: cssToggleButton,
              ref: toggleRef
            })
          }), _jsx(LiveRegion, {
            className: "pnr-section__live-region",
            politeness: POLITE,
            clearAnnouncementDelay: DELAY_TIME,
            onAnnouncementClear: clearAnnouncement,
            children: liveRegionText
          })]
        })]
      }), highlighted && _jsx("div", {
        className: "pnr-section__icon-error",
        css: cssErrorIcon,
        children: _jsx(IconError, {
          size: "small",
          "data-testid": "icon-error",
          title: highlightedMessage
        })
      })]
    }), _jsx(ExpandableTransition, {
      expanded: getExpanded(),
      duration: transitionDuration,
      id: `pnr-section-body-${uniqueId}`,
      className: "pnr-section__body",
      "aria-hidden": !getExpanded(),
      css: cssBody,
      children: _jsx("div", {
        className: "pnr-section__body-content",
        css: cssBodyContent,
        children: renderContent(children, getExpanded(), lazyLoad)
      })
    })]
  }));
}

Section.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Title of section, if `header` is not used. It is the tooltip for expand/collapse button. */
  title: PropTypes.string.isRequired,

  /**
   * If only `title` is provided, `aria-labelledby` will be used as the heading id.
   * Required when using `header`.
   */
  "aria-labelledby": PropTypes.string,

  /**
   * The body of the section. It is not required if `standalone` prop is
   * set to `true`.
   */
  children: requiredIf(PropTypes.node, props => !props.children && !props.standalone, "when `standalone` is not supplied."),

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * A function that returns the message for the expanded section.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when expand button is triggered.
     * It receives ${title} as an argument.
     * Defaults to `${title}, expanded`
     */
    expandedAnnouncement: PropTypes.func,

    /**
     * A function that returns the message for the collapsed section.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when expand button is triggered.
     * It receives ${title} as an argument.
     * Defaults to `${title}, collapsed`
     */
    collapsedAnnouncement: PropTypes.func,

    /**
     * aria-label for Expandable button.
     * This function will receive two arguments.
     * First argument is the section "${title}".
     * Second argument is a boolean value for the expanded state:
     * <ul>
     *  <li>
     *    `title`: Section title.
     *  </li>
     *  <li>
     *    `expanded`: Section expanded state.
     *  </li>
     * </ul>
     */
    expandButtonLabel: PropTypes.func
  }),

  /** Default value for Expanded when component is uncontrolled. */
  defaultExpanded: PropTypes.bool,

  /** Disable or enable the look of a button. */
  disabled: PropTypes.bool,

  /** Prop for Collapse or Expand Section when component is controlled. */
  expanded: PropTypes.bool,

  /**
   * The header of the section. Use `header` instead of `title` when HTML is required.
   * Section's title must be wrapped with heading(`h1` to `h6`).
   */
  header: PropTypes.node,

  /** Title should be rendered as a heading. Required when using only `title`. */
  headingLevel: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6"]),

  /** To highlight the section with error icon. */
  highlighted: PropTypes.bool,

  /** Error message that will be read by assistive tools when `highlighted` prop is set. */
  highlightedMessage: PropTypes.string,

  /** @ignore To change default collapse icon pass a valid icon node to this prop. */
  iconCollapse: validIcon,

  /** @ignore To change default expand icon pass a valid icon node to this prop. */
  iconExpand: validIcon,

  /** The style of Section. */
  kind: PropTypes.oneOf(["standard", "disclosureList"]),

  /**
   * Used to render Section body content when expanded.
   * <ul>
   *  <li>
   *    `false`: Section content is always rendered in the DOM.
   *     Collapse hides the Section body, without removing it from the DOM.
   *  </li>
   *  <li>
   *   `true`: Section content is rendered in DOM the first time
   *    Section is expanded. Collapse hides the Section content,
   *    without removing it from the DOM.
   *  </li>
   *  <li>
   *   `expandOnly`: Section content is only rendered when
   *    Section is expanded. Section content is always removed
   *    from DOM when Section is collapsed.
   *  </li>
   * </ul>
   */
  lazyLoad: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([EXPAND_ONLY])]),

  /** Callback to be invoked on expand/collapse. */
  onExpandedChange: PropTypes.func,

  /** Callback to be invoked on keydown events on the toggle button. */
  onToggleButtonKeyDown: PropTypes.func,

  /** Activate or deactivate collapse/expand button. */
  standalone: PropTypes.bool,

  /** @ignore To prevent animation, set to {0}. */
  transitionDuration: PropTypes.number
} : {};
Section.defaultProps = {
  header: null,
  headingLevel: null,
  expanded: null,
  defaultExpanded: false,
  children: null,
  standalone: false,
  disabled: false,
  highlighted: false,
  highlightedMessage: "Error",
  kind: "standard",
  "aria-labelledby": null,
  onExpandedChange: null,
  onToggleButtonKeyDown: null,
  lazyLoad: false,
  transitionDuration: 200,
  iconExpand: _jsx(IconExpand, {
    size: "medium"
  }),
  iconCollapse: _jsx(IconCollapse, {
    size: "medium"
  }),
  customStrings: null
};
Section.displayName = "Section";
export default Section;